/**
 * HomeHero — The contextual zone-1 of the SUPER AWENI home page.
 *
 * Renders SYNCHRONOUSLY at mount with whatever data is already available
 * (cached location, cached weather, locally-computed prayer times). Never
 * awaits any third-party API. If no location is set yet, renders a
 * priming card asking the user to choose a city.
 *
 * Layout (top to bottom):
 *   [city · temp · weather icon]  ← 56px context bar (tap = settings)
 *   [As-salaamu alaykum, {name}]
 *   [Next prayer in 1h 24m  →]    ← tap = prayer detail
 *
 * Empty/unset state: replaces the prayer line with a CTA "Choose your city"
 * that opens the LocationPrimingCard.
 *
 * MUST stay synchronous + side-effect-free: no fetch, no geolocation
 * prompt, no heavy compute. See AWENI/CLAUDE.md "Home Page Anti-Regression".
 */

import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import type { LocationService } from '../../services/LocationService';
import type { WeatherService } from '../../services/WeatherService';
import { gregorianToHijri, formatHijri } from '../../services/HijriDate';

export interface HomeHeroDeps {
  ctx: AweniContext;
  locationService: LocationService;
  weatherService: WeatherService;
  onChooseCity: () => void;
  onTapPrayer: () => void;
  onTapSettings: () => void;
}

export class HomeHero {
  private root: HTMLElement | null = null;
  private deps: HomeHeroDeps;

  constructor(deps: HomeHeroDeps) {
    this.deps = deps;
  }

  /** Render synchronously into the given parent element. */
  mount(parent: HTMLElement): void {
    const root = document.createElement('section');
    root.className = 'aweni-hero';
    root.setAttribute('aria-label', this.deps.ctx.i18n.t('home_hero_label') || 'Your daily summary');
    this.root = root;
    parent.appendChild(root);
    this.render();
  }

  /** Re-render the hero (called when location/weather/prayer data changes). */
  render(): void {
    if (!this.root) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const isRtl = this.deps.ctx.i18n.dir === 'rtl';
    const userName = this.deps.ctx.auth.user?.name?.split(' ')[0] || '';
    const isGuest = !!this.deps.ctx.auth.user?.is_guest;

    const loc = this.deps.locationService.getState();
    const weather = this.deps.weatherService.getData();
    const hasLocation = loc.lat !== null && loc.lng !== null;
    const cityName = loc.city || (hasLocation ? '' : t('home_hero_no_city'));

    let prayerLine = '';
    if (hasLocation) {
      try {
        const next = prayerTimeService.getNextPrayer();
        if (next) {
          const minsTotal = Math.max(0, Math.round(next.minutesUntil));
          const hours = Math.floor(minsTotal / 60);
          const mins = minsTotal % 60;
          const timeStr = hours > 0
            ? t('home_hero_in_hm', { h: String(hours), m: String(mins) })
            : t('home_hero_in_m', { m: String(mins) });
          prayerLine = t('home_hero_next_prayer', {
            prayer: t(`prayer_${next.name}`) || next.name,
            time: timeStr,
          });
        }
      } catch { /* prayer service unavailable — fall through to no-prayer state */ }
    }

    const greeting = isGuest
      ? t('home_hero_greeting_guest')
      : (userName ? t('home_hero_greeting_named', { name: userName }) : t('home_hero_greeting_anon'));

    const tempStr = weather && typeof weather.temperature === 'number' ? `${Math.round(weather.temperature)}°` : '';
    const weatherIcon = weather
      ? (weather.precipitationProbability > 60 ? 'rain'
          : weather.rain > 0 ? 'rain'
          : (weather.humidity || 0) > 80 ? 'cloud'
          : 'clear')
      : '';

    // Pin/refresh icon button — always visible in the context row. Lets
    // the user (a) explicitly re-trigger the location flow regardless of
    // current state, (b) switch city quickly without going to Settings.
    // Click → LocationPrimingCard which exposes both GPS + manual picker.
    const pinIcon = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;

    this.root.innerHTML = `
      <div class="aweni-hero-context-row">
        <div class="aweni-hero-context" data-action="settings" role="button" tabindex="0"
             aria-label="${t('home_hero_context_aria')}">
          <span class="aweni-hero-city">${cityName}</span>
          ${tempStr ? `<span class="aweni-hero-sep">·</span><span class="aweni-hero-temp">${tempStr}</span>` : ''}
          ${weatherIcon ? `<span class="aweni-hero-weather-icon">${this._weatherEmoji(weatherIcon)}</span>` : ''}
        </div>
        <button type="button" class="aweni-hero-pin-btn" data-action="change-city"
                aria-label="${t('home_hero_change_city') || 'Change city or refresh location'}">
          ${pinIcon}
        </button>
      </div>
      <h1 class="aweni-hero-greeting" dir="${isRtl ? 'rtl' : 'ltr'}">${greeting}</h1>
      <div class="aweni-hero-date" aria-label="${this._dateAriaLabel(t)}">
        ${this._formatTodayLine(this.deps.ctx.i18n.locale)}
      </div>
      ${hasLocation && prayerLine
        ? `<a class="aweni-hero-prayer" href="#/quran/prayer" data-action="prayer"
              aria-label="${prayerLine}">
             <span class="aweni-hero-prayer-text">${prayerLine}</span>
             <span class="aweni-hero-arrow">${isRtl ? '←' : '→'}</span>
           </a>`
        : `<button type="button" class="aweni-hero-choose-city" data-action="choose-city"
                   aria-label="${t('home_hero_choose_city')}">
             ${t('home_hero_choose_city')}
           </button>`}
    `;

    // Wire clicks (delegated since innerHTML overwrites listeners).
    const ctx = this.root.querySelector('.aweni-hero-context');
    ctx?.addEventListener('click', () => this.deps.onTapSettings());
    ctx?.addEventListener('keydown', (e) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Enter' || ke.key === ' ') { ke.preventDefault(); this.deps.onTapSettings(); }
    });
    const choose = this.root.querySelector('[data-action="choose-city"]');
    choose?.addEventListener('click', (e) => { e.preventDefault(); this.deps.onChooseCity(); });
    const pinBtn = this.root.querySelector('[data-action="change-city"]');
    pinBtn?.addEventListener('click', (e) => { e.preventDefault(); this.deps.onChooseCity(); });
    const prayer = this.root.querySelector('[data-action="prayer"]');
    prayer?.addEventListener('click', (e) => {
      // Allow native <a> hash navigation. Also notify listener for analytics.
      this.deps.onTapPrayer();
      // Don't preventDefault — let the hash route fire naturally.
      void e;
    });
  }

  unmount(): void {
    this.root?.remove();
    this.root = null;
  }

  /**
   * Single-line "today" label combining day-of-week + Gregorian + Hijri.
   * E.g.  "Wed · 30 Apr 2026 · 12 Shawwāl 1447 AH"
   * Computed entirely client-side, zero network. Localized per user locale.
   */
  private _formatTodayLine(locale: string): string {
    const now = new Date();
    let dayName = '';
    let gregorian = '';
    try {
      // Intl.DateTimeFormat for the day-name + Gregorian
      dayName = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(now);
      gregorian = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(now);
    } catch {
      // Locale not supported by ICU on this runtime — fall back to en-US.
      dayName = now.toLocaleDateString('en-US', { weekday: 'short' });
      gregorian = now.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
    }
    const hijri = formatHijri(gregorianToHijri(now), locale);
    return `${dayName} · ${gregorian} · ${hijri}`;
  }

  private _dateAriaLabel(t: (k: string) => string): string {
    return t('home_hero_date_aria') || "Today's date in Gregorian and Hijri";
  }

  /** Map WMO weather code or icon name to a single-emoji glyph. Cheap. */
  private _weatherEmoji(icon: string): string {
    const lc = icon.toLowerCase();
    if (lc.includes('clear') || lc === '☀') return '☀️';
    if (lc.includes('cloud')) return '☁️';
    if (lc.includes('rain')) return '🌧️';
    if (lc.includes('snow')) return '❄️';
    if (lc.includes('storm') || lc.includes('thunder')) return '⛈️';
    if (lc.includes('fog') || lc.includes('mist')) return '🌫️';
    return '🌤️';
  }
}
